import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Search,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  ChevronDown,
  Settings,
  MessageSquare,
  HelpCircle,
  UserRound,
} from "lucide-react";
import { useTutorial } from "./tutorial/useTutorial";
import { getTutorialForRole } from "../data/tutorials";
import { isCustomerOnboardingComplete } from "./CustomerOnboardingGate";
import BrandMark from "./BrandMark";
import ProfileAvatar from "./ProfileAvatar";

const NotificationBell = React.lazy(() => import("./NotificationBell"));
const ChatNavButton = React.lazy(() => import("./ChatNavButton"));

const NAV_LINKS = [
  { to: "/find-a-tutor", label: "Find a Tutor" },
  { to: "/subjects", label: "Subjects" },
  { to: "/about", label: "About Us" },
  { to: "/pricing", label: "Pricing" },
  { to: "/resources", label: "Resources" },
];

function Navbar() {
  const { user, logout } = useAuth();
  const { startTutorial } = useTutorial();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [userOpen, setUserOpen] = React.useState(false);
  const userMenuRef = React.useRef(null);

  const handleLogout = async () => {
    await logout();
    setUserOpen(false);
    navigate("/");
  };

  React.useEffect(() => {
    if (!userOpen) return;
    const handleOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setUserOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [userOpen]);

  return (
    <header
      className="sticky top-0 z-50 w-full"
      data-testid="site-navbar"
      style={{
        background: "rgba(251, 252, 255, 0.94)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        borderBottom: "1px solid rgba(48, 78, 226, 0.10)",
      }}
    >
      <div className="container-aus flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <BrandMark sizeClass="w-11 h-11" />
          <div className="flex flex-col leading-none">
            <span className="font-heading text-[1.35rem] font-bold text-aus-onyx tracking-tight">Austuition</span>
            <span className="text-[9px] font-bold tracking-[0.22em] uppercase mt-1 text-[#B92F58]">Online Tutoring</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `px-4 py-2.5 text-sm font-semibold rounded-full transition-all ${
                  isActive
                    ? "text-aus-onyx bg-[#FFD0DA]"
                    : "text-aus-onyx/75 hover:text-aus-onyx hover:bg-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <React.Suspense fallback={null}>
                <NotificationBell />
                <ChatNavButton />
              </React.Suspense>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl text-aus-onyx/80 hover:text-aus-onyx hover:bg-white/60"
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserOpen(!userOpen)}
                  className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-1.5 hover:bg-white/60 transition-colors"
                  data-testid="nav-user-menu"
                >
                  <ProfileAvatar
                    src={user.picture}
                    alt=""
                    className="w-7 h-7 shrink-0 rounded-full object-cover"
                    fallback={
                      <div
                        className="w-7 h-7 shrink-0 rounded-full text-white text-xs font-bold flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, #1E3FE0, #6366F1)" }}
                      >
                        {(user.name || user.email || "?").substring(0, 1).toUpperCase()}
                      </div>
                    }
                  />
                  <ChevronDown className="w-3.5 h-3.5 shrink-0 text-aus-onyx/70" />
                </button>
                {userOpen && (
                    <div className="absolute right-0 top-full mt-2 w-60 nav-user-dropdown overflow-hidden z-50 p-1.5">
                      <div className="px-3 py-2.5 border-b border-white/40">
                        <div className="text-[10px] text-aus-onyx/50 uppercase tracking-wider">Signed in as</div>
                        <div className="text-sm font-semibold truncate">{user.email}</div>
                        <div className="text-[10px] text-aus-cobalt uppercase tracking-wider mt-1">{user.role}</div>
                      </div>
                      <Link to="/dashboard" onClick={() => setUserOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-aus-cobalt-tint/60">
                        <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                      </Link>
                      {(user?.role === "tutor" || (user?.roles || []).includes("tutor")) ? (
                        <Link
                          to="/dashboard/edit-profile?tab=general"
                          onClick={() => setUserOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-aus-cobalt-tint/60"
                        >
                          <UserRound className="w-3.5 h-3.5" /> Edit profile
                        </Link>
                      ) : null}
                      <Link to="/messages" onClick={() => setUserOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-aus-cobalt-tint/60">
                        <MessageSquare className="w-3.5 h-3.5" /> Messages
                      </Link>
                      <Link to="/dashboard/settings" onClick={() => setUserOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-aus-cobalt-tint/60">
                        <Settings className="w-3.5 h-3.5" /> Settings
                      </Link>
                      <button
                        onClick={() => {
                          if (!isCustomerOnboardingComplete(user)) return;
                          setUserOpen(false);
                          const tutorialId = getTutorialForRole(user?.role);
                          startTutorial(tutorialId, true);
                        }}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-aus-cobalt-tint/60 ${
                          !isCustomerOnboardingComplete(user) ? "hidden" : ""
                        }`}
                      >
                        <HelpCircle className="w-3.5 h-3.5" /> Tutorial
                      </button>
                      <button
                        onClick={handleLogout}
                        data-testid="nav-logout"
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-red-50 text-red-700"
                      >
                        <LogOut className="w-3.5 h-3.5" /> Log out
                      </button>
                    </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-aus-onyx/75 hover:text-aus-onyx px-3 py-2 rounded-full hover:bg-white" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register" className="text-sm font-semibold text-aus-onyx px-4 py-2.5 rounded-full bg-white border-2 border-[#F5C2C7] hover:border-[#E84A6F] hover:text-[#B92F58] transition-colors" data-testid="nav-signup">
                Sign Up
              </Link>
              <Link to="/find-a-tutor" className="btn-pill-warm" style={{ padding: "0.7rem 1.4rem", fontSize: "0.9rem" }} data-testid="nav-find-tutor">
                <Search className="w-4 h-4" strokeWidth={2.3} /> Find a Tutor
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          {user ? (
            <React.Suspense fallback={null}>
              <NotificationBell />
              <ChatNavButton />
            </React.Suspense>
          ) : null}
          <button
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl p-2.5 hover:bg-white/60"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/30 bg-white/85 backdrop-blur-xl" data-testid="mobile-nav">
          <div className="container-aus py-3 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="min-h-11 px-3 py-2.5 rounded-xl text-sm font-medium text-aus-onyx hover:bg-aus-cobalt-tint/60 inline-flex items-center">
                {l.label}
              </Link>
            ))}
            <div className="h-px bg-white/40 my-2" />
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="min-h-11 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-aus-cobalt-tint/60 inline-flex items-center">Dashboard</Link>
                <Link to="/messages" onClick={() => setOpen(false)} className="min-h-11 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-aus-cobalt-tint/60 inline-flex items-center">Messages</Link>
                <Link to="/dashboard/settings" onClick={() => setOpen(false)} className="min-h-11 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-aus-cobalt-tint/60 inline-flex items-center">Settings</Link>
                <button onClick={handleLogout} className="min-h-11 px-3 py-2.5 rounded-xl text-sm font-medium text-left text-red-700">Log out</button>
              </>
            ) : (
              <div className="flex flex-col gap-2 mt-1">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary text-sm">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-secondary text-sm">Sign Up</Link>
                <Link to="/find-a-tutor" onClick={() => setOpen(false)} className="btn-pill-warm" style={{ padding: "0.7rem 1.4rem", fontSize: "0.9rem" }}><Search className="w-4 h-4" strokeWidth={2.3} /> Find a Tutor</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default React.memo(Navbar);
